/**
 * WerkstattKundeDetailPage — Werkstatt-Kunden-Detailansicht
 * Routing: /werkstatt/kunden/:id
 *
 * Tabs:
 *   • Stammdaten — liest CRM-Kontakt-Felder (Adresse, Kontakt, Notizen)
 *   • Fahrzeuge — Liste der dem Kunden zugeordneten Fahrzeuge
 *                 + Anlage-Dialog (Kennzeichen / Marke / Modell / VIN /
 *                                  Baujahr / Start-KM-Stand)
 *                 + KM-Stand-Historie pro Fahrzeug mit „Neuen KM-Stand
 *                   erfassen"-Button und Warnhinweis bei rückläufigem
 *                   KM-Stand
 *
 * Issue #402, Teil von #322 (Werkstatt-Kundenverwaltung).
 * Setzt voraus: Tabellen `fahrzeuge` (mit kontakt_id) und
 *               `fahrzeug_km_historie` — Migration 20260421_…sql.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import {
  AlertTriangle, ArrowLeft, Calendar, Car, ChevronRight, Edit3,
  Gauge, Loader2, Mail, MapPin, Phone, Plus, Trash2, User, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  isRueckläufig,
  sortHistorieByDatum,
  type FahrzeugKMHistorieEintrag,
} from '@/lib/werkstatt-fahrzeug-historie';
import { localIsoDate } from '@/lib/date-utils';

// ============================================================================
// TYPES
// ============================================================================

type TabKey = 'stammdaten' | 'fahrzeuge';

interface WerkstattKunde {
  id: string;
  name: string;
  firma: string | null;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  adresse: string | null;
  plz: string | null;
  ort: string | null;
  notizen: string | null;
}

interface WerkstattFahrzeug {
  id: string;
  kontakt_id: string;
  kennzeichen: string | null;
  marke: string | null;
  modell: string | null;
  fin: string | null;
  baujahr: number | null;
  km_aktuell: number | null;
  km_letzte_aktualisierung: string | null;
}

interface NeuesFahrzeugForm {
  kennzeichen: string;
  marke: string;
  modell: string;
  fin: string;
  baujahr: string;
  start_km: string;
}

const EMPTY_FAHRZEUG_FORM: NeuesFahrzeugForm = {
  kennzeichen: '',
  marke: '',
  modell: '',
  fin: '',
  baujahr: '',
  start_km: '',
};

// ============================================================================
// HAUPTKOMPONENTE
// ============================================================================

export default function WerkstattKundeDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const kundeId = params?.id;

  const [activeTab, setActiveTab] = useState<TabKey>('stammdaten');
  const [kunde, setKunde] = useState<WerkstattKunde | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!kundeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('crm_kontakte')
        .select(
          'id, name, firma, email, telefon, mobil, adresse, plz, ort, notizen',
        )
        .eq('id', kundeId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else if (!data) {
        setError('Werkstatt-Kunde nicht gefunden');
      } else {
        setKunde(data as WerkstattKunde);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [kundeId]);

  if (!kundeId) {
    return <NotFound onBack={() => setLocation('/werkstatt/kunden')} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="werkstatt-kunde-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !kunde) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Kunde nicht verfügbar</h2>
        <p className="text-sm text-gray-500 mb-4">{error || 'Unbekannter Fehler.'}</p>
        <Link
          href="/werkstatt/kunden"
          className="text-sm text-[#B91C1C] hover:underline inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück zur Kundenliste
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          href="/werkstatt/kunden"
          className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Kunden
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
        <span className="text-sm font-medium text-gray-900 truncate">
          {kunde.name || 'Unbenannter Kunde'}
        </span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 bg-[#B91C1C] rounded-lg flex items-center justify-center text-white font-bold">
            {(kunde.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {kunde.name || 'Unbenannt'}
            </h1>
            {kunde.firma && (
              <p className="text-sm text-gray-500 truncate">{kunde.firma}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-100">
          <TabButton
            active={activeTab === 'stammdaten'}
            onClick={() => setActiveTab('stammdaten')}
            icon={<User className="w-4 h-4" />}
            label="Stammdaten"
          />
          <TabButton
            active={activeTab === 'fahrzeuge'}
            onClick={() => setActiveTab('fahrzeuge')}
            icon={<Car className="w-4 h-4" />}
            label="Fahrzeuge"
          />
        </div>

        <div className="p-5">
          {activeTab === 'stammdaten' ? (
            <StammdatenPanel kunde={kunde} />
          ) : (
            <FahrzeugePanel kundeId={kunde.id} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// STAMMDATEN
// ============================================================================

function StammdatenPanel({ kunde }: { kunde: WerkstattKunde }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
      <Field icon={<User className="w-4 h-4" />} label="Name" value={kunde.name} />
      <Field icon={<User className="w-4 h-4" />} label="Firma" value={kunde.firma} />
      <Field icon={<Mail className="w-4 h-4" />} label="E-Mail" value={kunde.email} />
      <Field icon={<Phone className="w-4 h-4" />} label="Telefon" value={kunde.telefon} />
      <Field icon={<Phone className="w-4 h-4" />} label="Mobil" value={kunde.mobil} />
      <Field
        icon={<MapPin className="w-4 h-4" />}
        label="Adresse"
        value={
          [kunde.adresse, [kunde.plz, kunde.ort].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ') || null
        }
      />
      {kunde.notizen && (
        <div className="sm:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-gray-500 mb-1">Notizen</dt>
          <dd className="whitespace-pre-wrap text-gray-700">{kunde.notizen}</dd>
        </div>
      )}
    </dl>
  );
}

// ============================================================================
// FAHRZEUGE
// ============================================================================

function FahrzeugePanel({ kundeId }: { kundeId: string }) {
  const [fahrzeuge, setFahrzeuge] = useState<WerkstattFahrzeug[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedFahrzeugId, setSelectedFahrzeugId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('fahrzeuge')
      .select(
        'id, kontakt_id, kennzeichen, marke, modell, fin, baujahr, km_aktuell, km_letzte_aktualisierung',
      )
      .eq('kontakt_id', kundeId)
      .order('kennzeichen', { ascending: true });
    if (error) {
      toast.error(`Fahrzeuge konnten nicht geladen werden: ${error.message}`);
      setFahrzeuge([]);
    } else {
      setFahrzeuge((data ?? []) as WerkstattFahrzeug[]);
    }
    setLoading(false);
  }, [kundeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selected = useMemo(
    () => fahrzeuge.find((f) => f.id === selectedFahrzeugId) ?? null,
    [fahrzeuge, selectedFahrzeugId],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">
          Fahrzeuge {fahrzeuge.length > 0 && (
            <span className="text-gray-400">({fahrzeuge.length})</span>
          )}
        </h2>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="werkstatt-fahrzeug-anlegen"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#B91C1C] text-white rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors"
        >
          <Plus className="w-4 h-4" /> Fahrzeug anlegen
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : fahrzeuge.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg p-8 text-center">
          <Car className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Noch keine Fahrzeuge zugeordnet</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Kennzeichen</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Marke</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Modell</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 hidden md:table-cell">VIN</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">KM-Stand</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 hidden lg:table-cell">Letzte Aktualisierung</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {fahrzeuge.map((f) => (
                <tr
                  key={f.id}
                  data-testid="werkstatt-fahrzeug-row"
                  onClick={() => setSelectedFahrzeugId(f.id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2 font-medium text-gray-900">{f.kennzeichen || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{f.marke || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{f.modell || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 font-mono text-xs hidden md:table-cell">
                    {f.fin || '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">
                    {f.km_aktuell != null ? f.km_aktuell.toLocaleString('de-DE') : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 hidden lg:table-cell">
                    {f.km_letzte_aktualisierung
                      ? new Date(f.km_letzte_aktualisierung).toLocaleDateString('de-DE')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <FahrzeugAnlegenDialog
          kundeId={kundeId}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {selected && (
        <FahrzeugDetailDialog
          fahrzeug={selected}
          onClose={() => setSelectedFahrzeugId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

// ============================================================================
// FAHRZEUG-ANLAGE-DIALOG
// ============================================================================

function FahrzeugAnlegenDialog({
  kundeId,
  onClose,
  onCreated,
}: {
  kundeId: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [form, setForm] = useState<NeuesFahrzeugForm>(EMPTY_FAHRZEUG_FORM);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.kennzeichen.trim() || !form.marke.trim() || !form.modell.trim()) {
      toast.error('Kennzeichen, Marke und Modell sind Pflichtfelder.');
      return;
    }
    const startKM = form.start_km.trim() === '' ? null : Number(form.start_km);
    if (startKM != null && (!Number.isFinite(startKM) || startKM < 0)) {
      toast.error('Start-KM-Stand muss eine positive Zahl sein.');
      return;
    }
    const baujahr = form.baujahr.trim() === '' ? null : Number(form.baujahr);
    if (baujahr != null && (!Number.isInteger(baujahr) || baujahr < 1900 || baujahr > 2100)) {
      toast.error('Baujahr muss eine sinnvolle Jahreszahl sein.');
      return;
    }

    setSaving(true);
    const { data: newFahrzeug, error: insertErr } = await supabase
      .from('fahrzeuge')
      .insert({
        kontakt_id: kundeId,
        kennzeichen: form.kennzeichen.trim().toUpperCase(),
        marke: form.marke.trim(),
        modell: form.modell.trim(),
        fin: form.fin.trim() || null,
        baujahr,
        km_aktuell: startKM,
        km_letzte_aktualisierung:
          startKM != null ? localIsoDate(new Date()) : null,
      })
      .select('id')
      .single();
    if (insertErr || !newFahrzeug) {
      setSaving(false);
      toast.error(`Anlage fehlgeschlagen: ${insertErr?.message || 'unbekannt'}`);
      return;
    }

    if (startKM != null) {
      const { error: histErr } = await supabase.from('fahrzeug_km_historie').insert({
        fahrzeug_id: newFahrzeug.id,
        km_stand: startKM,
        quelle: 'anlage',
        notiz: 'Start-KM-Stand bei Fahrzeug-Anlage',
      });
      if (histErr) {
        toast.error(
          `Fahrzeug angelegt, aber KM-Historie konnte nicht geschrieben werden: ${histErr.message}`,
        );
      }
    }

    setSaving(false);
    toast.success('Fahrzeug angelegt');
    await onCreated();
  };

  return (
    <Dialog onClose={onClose} title="Neues Fahrzeug anlegen" testId="werkstatt-fahrzeug-dialog">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FieldInput
          label="Kennzeichen *"
          value={form.kennzeichen}
          onChange={(v) => setForm((p) => ({ ...p, kennzeichen: v }))}
          placeholder="MK-AB 1234"
          uppercase
          testId="input-kennzeichen"
        />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput
            label="Marke *"
            value={form.marke}
            onChange={(v) => setForm((p) => ({ ...p, marke: v }))}
            placeholder="BMW"
            testId="input-marke"
          />
          <FieldInput
            label="Modell *"
            value={form.modell}
            onChange={(v) => setForm((p) => ({ ...p, modell: v }))}
            placeholder="320d"
            testId="input-modell"
          />
        </div>
        <FieldInput
          label="VIN (FIN)"
          value={form.fin}
          onChange={(v) => setForm((p) => ({ ...p, fin: v }))}
          placeholder="WBA…"
          uppercase
          testId="input-fin"
        />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput
            label="Baujahr"
            value={form.baujahr}
            onChange={(v) => setForm((p) => ({ ...p, baujahr: v.replace(/\D/g, '') }))}
            placeholder="2018"
            inputMode="numeric"
            testId="input-baujahr"
          />
          <FieldInput
            label="Start-KM-Stand"
            value={form.start_km}
            onChange={(v) => setForm((p) => ({ ...p, start_km: v.replace(/\D/g, '') }))}
            placeholder="56000"
            inputMode="numeric"
            testId="input-start-km"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="werkstatt-fahrzeug-speichern"
            className="px-4 py-2 text-sm bg-[#B91C1C] text-white rounded-lg font-medium hover:bg-[#991B1B] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Anlegen
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================================
// FAHRZEUG-DETAIL + KM-HISTORIE
// ============================================================================

function FahrzeugDetailDialog({
  fahrzeug,
  onClose,
  onChanged,
}: {
  fahrzeug: WerkstattFahrzeug;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [historie, setHistorie] = useState<FahrzeugKMHistorieEintrag[]>([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [showKMDialog, setShowKMDialog] = useState(false);
  const [editing, setEditing] = useState(false);

  const reloadHistorie = useCallback(async () => {
    setLoadingHist(true);
    const { data, error } = await supabase
      .from('fahrzeug_km_historie')
      .select('id, fahrzeug_id, km_stand, erfasst_am, quelle, notiz')
      .eq('fahrzeug_id', fahrzeug.id);
    if (error) {
      toast.error(`KM-Historie konnte nicht geladen werden: ${error.message}`);
      setHistorie([]);
    } else {
      setHistorie(sortHistorieByDatum((data ?? []) as FahrzeugKMHistorieEintrag[]));
    }
    setLoadingHist(false);
  }, [fahrzeug.id]);

  useEffect(() => {
    void reloadHistorie();
  }, [reloadHistorie]);

  const handleDelete = async () => {
    if (!window.confirm('Fahrzeug wirklich löschen? Die KM-Historie wird mitgelöscht.')) return;
    const { error } = await supabase.from('fahrzeuge').delete().eq('id', fahrzeug.id);
    if (error) {
      toast.error(`Löschen fehlgeschlagen: ${error.message}`);
      return;
    }
    toast.success('Fahrzeug gelöscht');
    await onChanged();
    onClose();
  };

  return (
    <Dialog
      onClose={onClose}
      title={`${fahrzeug.kennzeichen ?? 'Fahrzeug'} — ${fahrzeug.marke ?? ''} ${fahrzeug.modell ?? ''}`.trim()}
      testId="werkstatt-fahrzeug-detail"
      wide
    >
      <div className="space-y-4">
        {editing ? (
          <FahrzeugBearbeitenForm
            fahrzeug={fahrzeug}
            onCancel={() => setEditing(false)}
            onSaved={async () => {
              setEditing(false);
              await onChanged();
            }}
          />
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <Field icon={<Car className="w-4 h-4" />} label="Kennzeichen" value={fahrzeug.kennzeichen} />
            <Field icon={<Car className="w-4 h-4" />} label="Marke" value={fahrzeug.marke} />
            <Field icon={<Car className="w-4 h-4" />} label="Modell" value={fahrzeug.modell} />
            <Field icon={<Car className="w-4 h-4" />} label="VIN" value={fahrzeug.fin} />
            <Field
              icon={<Calendar className="w-4 h-4" />}
              label="Baujahr"
              value={fahrzeug.baujahr != null ? String(fahrzeug.baujahr) : null}
            />
            <Field
              icon={<Gauge className="w-4 h-4" />}
              label="Aktueller KM-Stand"
              value={fahrzeug.km_aktuell != null ? fahrzeug.km_aktuell.toLocaleString('de-DE') : null}
            />
          </dl>
        )}

        {!editing && (
          <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg inline-flex items-center gap-1.5"
            >
              <Edit3 className="w-3.5 h-3.5" /> Bearbeiten
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
              <Gauge className="w-4 h-4 text-gray-400" /> KM-Stand-Historie
            </h3>
            <button
              type="button"
              onClick={() => setShowKMDialog(true)}
              data-testid="werkstatt-km-erfassen"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#B91C1C] text-white rounded-lg text-xs font-medium hover:bg-[#991B1B]"
            >
              <Plus className="w-3.5 h-3.5" /> Neuen KM-Stand erfassen
            </button>
          </div>
          {loadingHist ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : historie.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              Noch keine KM-Stände erfasst
            </p>
          ) : (
            <KMHistorieTabelle eintraege={historie} />
          )}
        </div>
      </div>

      {showKMDialog && (
        <KMErfassenDialog
          fahrzeugId={fahrzeug.id}
          historie={historie}
          onClose={() => setShowKMDialog(false)}
          onSaved={async () => {
            setShowKMDialog(false);
            await reloadHistorie();
            await onChanged();
          }}
        />
      )}
    </Dialog>
  );
}

function KMHistorieTabelle({ eintraege }: { eintraege: FahrzeugKMHistorieEintrag[] }) {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-gray-50/50 border-b border-gray-100">
          <tr>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Datum</th>
            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">KM-Stand</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Quelle</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Notiz</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50" data-testid="werkstatt-km-historie">
          {eintraege.map((e) => (
            <tr key={e.id} data-testid="werkstatt-km-historie-row">
              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                {new Date(e.erfasst_am).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-900">
                {e.km_stand.toLocaleString('de-DE')}
              </td>
              <td className="px-3 py-2 text-gray-500 text-xs">{e.quelle || 'manuell'}</td>
              <td className="px-3 py-2 text-gray-500 text-xs">{e.notiz || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KMErfassenDialog({
  fahrzeugId,
  historie,
  onClose,
  onSaved,
}: {
  fahrzeugId: string;
  historie: FahrzeugKMHistorieEintrag[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [kmInput, setKmInput] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmedRueckläufig, setConfirmedRueckläufig] = useState(false);

  const parsedKM = kmInput.trim() === '' ? NaN : Number(kmInput);
  const isValid = Number.isFinite(parsedKM) && parsedKM >= 0;
  const ruecklaeufig = isValid && isRueckläufig(parsedKM, historie);
  const blockedByWarning = ruecklaeufig && !confirmedRueckläufig;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      toast.error('Bitte einen gültigen KM-Stand eingeben.');
      return;
    }
    if (blockedByWarning) {
      toast.error('Bitte rückläufigen KM-Stand zuerst bestätigen.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('fahrzeug_km_historie').insert({
      fahrzeug_id: fahrzeugId,
      km_stand: parsedKM,
      quelle: 'manuell',
      notiz: notiz.trim() || null,
    });
    setSaving(false);
    if (error) {
      toast.error(`Speichern fehlgeschlagen: ${error.message}`);
      return;
    }
    toast.success('KM-Stand erfasst');
    await onSaved();
  };

  return (
    <Dialog onClose={onClose} title="Neuen KM-Stand erfassen" testId="werkstatt-km-dialog">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FieldInput
          label="KM-Stand *"
          value={kmInput}
          onChange={(v) => {
            setKmInput(v.replace(/\D/g, ''));
            setConfirmedRueckläufig(false);
          }}
          placeholder="65000"
          inputMode="numeric"
          testId="input-km-stand"
        />
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Notiz</label>
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            rows={2}
            placeholder="z.B. Inspektion, Reifenwechsel"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none resize-none"
          />
        </div>

        {ruecklaeufig && (
          <div
            data-testid="werkstatt-km-warnung"
            className="bg-amber-50 border border-amber-200 rounded-lg p-3"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Rückläufiger KM-Stand</p>
                <p className="text-xs mt-1">
                  Der eingegebene KM-Stand ist niedriger als der bisherige Höchstwert. Bitte bestätigen,
                  wenn das beabsichtigt ist (z.B. Tachotausch).
                </p>
                <label className="inline-flex items-center gap-2 mt-2 text-xs">
                  <input
                    type="checkbox"
                    checked={confirmedRueckläufig}
                    onChange={(e) => setConfirmedRueckläufig(e.target.checked)}
                    data-testid="werkstatt-km-warnung-bestaetigen"
                  />
                  Ja, KM-Stand ist trotzdem korrekt
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving || !isValid || blockedByWarning}
            data-testid="werkstatt-km-speichern"
            className="px-4 py-2 text-sm bg-[#B91C1C] text-white rounded-lg font-medium hover:bg-[#991B1B] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Speichern
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function FahrzeugBearbeitenForm({
  fahrzeug,
  onCancel,
  onSaved,
}: {
  fahrzeug: WerkstattFahrzeug;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form, setForm] = useState({
    kennzeichen: fahrzeug.kennzeichen ?? '',
    marke: fahrzeug.marke ?? '',
    modell: fahrzeug.modell ?? '',
    fin: fahrzeug.fin ?? '',
    baujahr: fahrzeug.baujahr != null ? String(fahrzeug.baujahr) : '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const baujahr = form.baujahr.trim() === '' ? null : Number(form.baujahr);
    if (baujahr != null && (!Number.isInteger(baujahr) || baujahr < 1900 || baujahr > 2100)) {
      toast.error('Baujahr muss eine sinnvolle Jahreszahl sein.');
      setSaving(false);
      return;
    }
    const { error } = await supabase
      .from('fahrzeuge')
      .update({
        kennzeichen: form.kennzeichen.trim().toUpperCase(),
        marke: form.marke.trim(),
        modell: form.modell.trim(),
        fin: form.fin.trim() || null,
        baujahr,
      })
      .eq('id', fahrzeug.id);
    setSaving(false);
    if (error) {
      toast.error(`Speichern fehlgeschlagen: ${error.message}`);
      return;
    }
    toast.success('Fahrzeug aktualisiert');
    await onSaved();
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <FieldInput
        label="Kennzeichen"
        value={form.kennzeichen}
        onChange={(v) => setForm((p) => ({ ...p, kennzeichen: v }))}
        uppercase
      />
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="Marke" value={form.marke} onChange={(v) => setForm((p) => ({ ...p, marke: v }))} />
        <FieldInput label="Modell" value={form.modell} onChange={(v) => setForm((p) => ({ ...p, modell: v }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FieldInput
          label="VIN"
          value={form.fin}
          onChange={(v) => setForm((p) => ({ ...p, fin: v }))}
          uppercase
        />
        <FieldInput
          label="Baujahr"
          value={form.baujahr}
          onChange={(v) => setForm((p) => ({ ...p, baujahr: v.replace(/\D/g, '') }))}
          inputMode="numeric"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-[#B91C1C] text-white rounded-lg font-medium hover:bg-[#991B1B] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Speichern
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// HILFSKOMPONENTEN
// ============================================================================

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-[#B91C1C] text-[#B91C1C]'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon} {label}
    </button>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500 mb-1 inline-flex items-center gap-1.5">
        {icon} {label}
      </dt>
      <dd className="text-gray-900">{value || <span className="text-gray-400">—</span>}</dd>
    </div>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  placeholder,
  uppercase,
  inputMode,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  uppercase?: boolean;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  testId?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        data-testid={testId}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none ${
          uppercase ? 'uppercase' : ''
        }`}
      />
    </label>
  );
}

function Dialog({
  onClose,
  title,
  children,
  testId,
  wide,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  testId?: string;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        data-testid={testId}
        className={`bg-white rounded-2xl shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="max-w-lg mx-auto p-6 text-center">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Kein Kunde ausgewählt</h2>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[#B91C1C] hover:underline"
      >
        Zur Kundenliste
      </button>
    </div>
  );
}
